/**
 * Cookie utility functions for managing browser cookies
 */

/**
 * Set a cookie with specified name, value, and expiration
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (default: 365)
 * @param {object} options - Additional cookie options
 */
export const setCookie = (name, value, days = 365, options = {}) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  const cookieOptions = {
    expires: expires.toUTCString(),
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
    ...options
  };

  const cookieString = Object.entries(cookieOptions)
    .map(([key, value]) => `${key}=${value}`)
    .join(';');

  document.cookie = `${name}=${encodeURIComponent(value)};${cookieString}`;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    let c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
};

/**
 * Remove a cookie by setting it to expire
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const removeCookie = (name, path = '/') => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};`;
};

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} True if cookie exists
 */
export const hasCookie = (name) => {
  return getCookie(name) !== null;
};

/**
 * Get all cookies as an object
 * @returns {object} Object with cookie names as keys and values as values
 */
export const getAllCookies = () => {
  const cookies = {};
  const cookieArray = document.cookie.split(';');

  for (let cookie of cookieArray) {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[name] = decodeURIComponent(value);
    }
  }

  return cookies;
};

/**
 * Clear all cookies (be careful with this!)
 */
export const clearAllCookies = () => {
  const cookies = getAllCookies();
  Object.keys(cookies).forEach(name => removeCookie(name));
};